import { createFileRoute } from "@tanstack/react-router"
import { trpc } from "lib/trpc"
import { Circle, CircleCheckBig } from "lucide-react"
import { useMemo, useState } from "react"
import { ClientHeader } from "@/components/client-header"
import { ClientTabNavigation } from "@/components/client-tab-navigation"
import { TaskListItem } from "@/components/display/task-list-item"
import { TaskModal } from "@/components/modals/task-modal"
import { Page } from "@/components/page"
import { ProgressRing } from "@/components/progress-ring"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import type { RouterOutputs } from "../../../../../../../api/routes/api/router"

type Priority = "HIGH" | "MEDIUM" | "LOW"

type ChecklistItem = {
  key: string
  label: string
  priority: Priority
  complete: boolean
}

type Task = RouterOutputs["tasks"]["list"][number]

export const Route = createFileRoute("/_authed/_professional/professional/overview/$estateId")({
  component: RouteComponent,
})

function RouteComponent() {
  const { estateId } = Route.useParams()
  const { data: tasks = [] } = trpc.tasks.list.useQuery({ estateId })

  return (
    <Page
      headerSlot={<ClientHeader estateId={estateId} />}
      tabSlot={<ClientTabNavigation estateId={estateId} />}
    >
      <div className="space-y-6">
        <EstateChecklistPanel estateId={estateId} />
        <TasksPanel estateId={estateId} tasks={tasks} />
      </div>
    </Page>
  )
}

/* ---------------------------
   Estate Planning Checklist
---------------------------- */
function ChecklistRow({ label, complete }: { label: string; complete: boolean }) {
  return (
    <div className="flex items-center gap-2">
      {complete ? (
        <CircleCheckBig className="size-4 text-success" aria-label="complete" />
      ) : (
        <Circle className="size-4 text-muted-foreground" aria-label="incomplete" />
      )}
      <span
        className={`text-sm ${complete ? "text-success line-through" : "text-muted-foreground"}`}
      >
        {label}
      </span>
    </div>
  )
}

function EstateChecklistPanel({ estateId }: { estateId: string }) {
  const enabled = Boolean(estateId)

  const { data: assets = [], isLoading: assetsLoading } = trpc.assets.list.useQuery(
    { estateId },
    { enabled },
  )
  const { data: contacts = [], isLoading: contactsLoading } = trpc.contacts.list.useQuery(
    { estateId },
    { enabled },
  )
  const { data: documents = [], isLoading: documentsLoading } = trpc.documents.list.useQuery(
    { estateId },
    { enabled },
  )
  const { data: insurance = [], isLoading: insuranceLoading } = trpc.insurance.list.useQuery(
    { estateId },
    { enabled },
  )
  const { data: companies = [], isLoading: companiesLoading } = trpc.company.list.useQuery(
    { estateId },
    { enabled },
  )
  const { data: trusts = [], isLoading: trustsLoading } = trpc.trusts.list.useQuery(
    { estateId },
    { enabled },
  )

  const { data: allInvitations = [], isLoading: invitationsLoading } =
    trpc.invitations.list.useQuery({})
  const invitations = useMemo(
    () => allInvitations.filter((i) => i.estateId === estateId),
    [allInvitations, estateId],
  )

  const isLoading =
    assetsLoading ||
    contactsLoading ||
    documentsLoading ||
    insuranceLoading ||
    companiesLoading ||
    trustsLoading ||
    invitationsLoading

  const hasWill = useMemo(
    () => documents.some((d) => d.type === "LAST_WILL_AND_TESTAMENT"),
    [documents],
  )
  const hasPowerOfAttorney = useMemo(
    () => documents.some((d) => d.type === "POWER_OF_ATTORNEY"),
    [documents],
  )
  const hasExecutor = useMemo(
    () => contacts.some((c) => c.estateRoles?.some((r) => r.estateRole === "EXECUTOR")),
    [contacts],
  )
  const hasBankingDetails = useMemo(
    () =>
      assets.some((a) => ["BANK_ACCOUNT", "BANK_ACCOUNT_OR_CASH", "SAVINGS"].includes(a.assetType)),
    [assets],
  )
  const hasInsuranceDetails = useMemo(() => insurance.length > 0, [insurance])
  const hasInvestmentDetails = useMemo(
    () =>
      assets.some((a) =>
        [
          "INVESTMENT",
          "INVESTMENT_ACCOUNT",
          "STOCK_AND_BONDS",
          "401K",
          "IRA",
          "CORP_PENSION",
          "GOV_PENSION",
          "RETIREMENT_ACCOUNT",
          "RRSP",
          "TFSA",
        ].includes(a.assetType),
      ),
    [assets],
  )
  const hasCollaborators = useMemo(() => invitations.length > 0, [invitations])
  const hasBusinessDetails = useMemo(() => companies.length > 0, [companies])
  const hasTrustDocuments = useMemo(() => trusts.length > 0, [trusts])
  const hasPropertyDetails = useMemo(
    () =>
      assets.some((a) =>
        ["REAL_ESTATE", "PRIMARY_RESIDENCE", "RENTAL_PROPERTY"].includes(a.assetType),
      ),
    [assets],
  )
  const hasLawyer = useMemo(
    () => contacts.some((c) => c.estateRoles?.some((r) => r.estateRole === "LEGAL_PROFESSIONAL")),
    [contacts],
  )
  const hasAccountant = useMemo(
    () => contacts.some((c) => c.estateRoles?.some((r) => r.estateRole === "TAX_PROFESSIONAL")),
    [contacts],
  )
  const hasHealthDirectives = useMemo(
    () => documents.some((d) => d.type === "MEDICAL_DIRECTIVE"),
    [documents],
  )
  const hasVendors = useMemo(
    () => contacts.some((c) => c.estateRoles?.some((r) => r.estateRole === "PROFESSIONAL_ADVISOR")),
    [contacts],
  )
  const hasGuardianship = useMemo(
    () => contacts.some((c) => c.estateRoles?.some((r) => r.estateRole === "GUARDIAN")),
    [contacts],
  )

  const byPriority = useMemo(() => {
    const high: ChecklistItem[] = [
      {
        key: "LAST_WILL_AND_TESTAMENT",
        label: "Last Will and Testament",
        priority: "HIGH",
        complete: hasWill,
      },
      {
        key: "POWER_OF_ATTORNEY",
        label: "Power of Attorney",
        priority: "HIGH",
        complete: hasPowerOfAttorney,
      },
      { key: "EXECUTOR", label: "Executor", priority: "HIGH", complete: hasExecutor },
      {
        key: "BANKING_DETAILS",
        label: "Banking details",
        priority: "HIGH",
        complete: hasBankingDetails,
      },
      {
        key: "INSURANCE_DETAILS",
        label: "Insurance details",
        priority: "HIGH",
        complete: hasInsuranceDetails,
      },
      {
        key: "INVESTMENT_DETAILS",
        label: "Investment details",
        priority: "HIGH",
        complete: hasInvestmentDetails,
      },
      {
        key: "COLLABORATORS",
        label: "Collaborators",
        priority: "HIGH",
        complete: hasCollaborators,
      },
    ]

    const medium: ChecklistItem[] = [
      {
        key: "BUSINESS_DETAILS",
        label: "Business details",
        priority: "MEDIUM",
        complete: hasBusinessDetails,
      },
      {
        key: "TRUST_DOCUMENTS",
        label: "Trust documents",
        priority: "MEDIUM",
        complete: hasTrustDocuments,
      },
      {
        key: "PROPERTY_DETAILS",
        label: "Property details",
        priority: "MEDIUM",
        complete: hasPropertyDetails,
      },
      { key: "LAWYER", label: "Lawyer", priority: "MEDIUM", complete: hasLawyer },
      { key: "ACCOUNTANT", label: "Accountant", priority: "MEDIUM", complete: hasAccountant },
      {
        key: "HEALTH_DIRECTIVES",
        label: "Health directives",
        priority: "MEDIUM",
        complete: hasHealthDirectives,
      },
    ]

    const low: ChecklistItem[] = [
      { key: "VENDORS", label: "Vendors", priority: "LOW", complete: hasVendors },
      { key: "GUARDIANSHIP", label: "Guardianship", priority: "LOW", complete: hasGuardianship },
    ]

    return { high, medium, low }
  }, [
    hasWill,
    hasPowerOfAttorney,
    hasExecutor,
    hasBankingDetails,
    hasInsuranceDetails,
    hasInvestmentDetails,
    hasCollaborators,
    hasBusinessDetails,
    hasTrustDocuments,
    hasPropertyDetails,
    hasLawyer,
    hasAccountant,
    hasHealthDirectives,
    hasVendors,
    hasGuardianship,
  ])

  const percent = useMemo(() => {
    if (byPriority.high.length === 0) return 0
    const completed = byPriority.high.filter((i) => i.complete).length
    return Math.round((completed / byPriority.high.length) * 100)
  }, [byPriority.high])

  return (
    <Card>
      <CardContent>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-lg font-semibold">Estate Planning Checklist</div>
            <div className="text-sm text-muted-foreground">
              Focus on completing high priority items first
            </div>
          </div>
          <ProgressRing percent={percent} ariaLabel="Estate checklist completion ring" />
        </div>

        {isLoading ? (
          <div className="mt-5 text-sm text-muted-foreground">Loading checklist…</div>
        ) : (
          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-7">
            <div>
              <div className="text-sm font-semibold mb-3">High priority</div>
              <div className="space-y-2">
                {byPriority.high.map((i) => (
                  <ChecklistRow key={i.key} label={i.label} complete={i.complete} />
                ))}
              </div>
            </div>

            <div>
              <div className="text-sm font-semibold mb-3">Medium priority</div>
              <div className="space-y-2">
                {byPriority.medium.map((i) => (
                  <ChecklistRow key={i.key} label={i.label} complete={i.complete} />
                ))}
              </div>
            </div>

            <div>
              <div className="text-sm font-semibold mb-3">Low priority</div>
              <div className="space-y-2">
                {byPriority.low.map((i) => (
                  <ChecklistRow key={i.key} label={i.label} complete={i.complete} />
                ))}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

/* ---------------------------
   Tasks Panel
---------------------------- */
function TasksPanel({ estateId, tasks }: { estateId: string; tasks: Task[] }) {
  const utils = trpc.useUtils()
  const [taskModalOpen, setTaskModalOpen] = useState(false)
  const [editingTask, setEditingTask] = useState<Task | null>(null)

  const updateTask = trpc.tasks.update.useMutation({
    onSuccess: () => utils.tasks.list.invalidate({ estateId }),
  })

  const deleteTask = trpc.tasks.delete.useMutation({
    onSuccess: () => utils.tasks.list.invalidate({ estateId }),
  })

  const incompleteTasks = useMemo(() => tasks.filter((t) => !(t.taskComplete ?? false)), [tasks])
  const completedTasks = useMemo(() => tasks.filter((t) => t.taskComplete ?? false), [tasks])

  const MAX_VISIBLE_TASKS = 3
  const needsScroll = tasks.length > MAX_VISIBLE_TASKS

  const renderTask = (t: Task) => (
    <TaskListItem
      key={t.id}
      task={t}
      onToggleComplete={(task) =>
        updateTask.mutate({
          id: task.id,
          taskComplete: !(task.taskComplete ?? false),
          completionDate: !(task.taskComplete ?? false) ? new Date().toISOString() : null,
        })
      }
      onEdit={(task) => {
        setEditingTask(task)
        setTaskModalOpen(true)
      }}
      onDelete={(taskId) => deleteTask.mutate({ id: taskId })}
    />
  )

  return (
    <Card>
      <CardContent>
        <div className="relative overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <div className="text-lg font-semibold">Tasks</div>

            <TaskModal
              estateId={estateId}
              open={taskModalOpen}
              setOpen={(open) => {
                setTaskModalOpen(open)
                if (!open) setEditingTask(null)
              }}
              task={editingTask}
              triggerSlot={
                <Button variant="default" size="sm" onClick={() => setTaskModalOpen(true)}>
                  + Add task
                </Button>
              }
            />
          </div>

          <div className="relative h-full z-0">
            <div className={cn("overflow-y-auto", needsScroll && "max-h-72 pb-6")}>
              {tasks.length === 0 ? (
                <div className="text-sm text-muted-foreground italic">No tasks.</div>
              ) : (
                <div className="space-y-3">
                  {incompleteTasks.map(renderTask)}

                  {completedTasks.length > 0 && (
                    <>
                      <div className="flex items-center gap-3 pt-2">
                        <hr className="flex-1 border-muted" />
                        <p className="text-xs mb-1 font-medium text-muted-foreground uppercase tracking-wide">
                          Completed tasks
                        </p>
                        <hr className="flex-1 border-muted" />
                      </div>

                      {completedTasks.map(renderTask)}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {needsScroll && (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 left-0 right-0 h-20 bg-white-to-t from-muted/100 to-transparent z-10" />
          )}
        </div>
      </CardContent>
    </Card>
  )
}
